import { RefObject, useCallback, useRef } from 'react';

interface ScrollToAndFocusOptions {
  scrollBehavior?: ScrollBehavior; //Behavior for scrolling. Defaults to 'smooth'
  scrollBlock?: ScrollLogicalPosition; //Vertical alignment. Defaults to 'start'
  scrollInline?: ScrollLogicalPosition; //Horizontal alignment. Defaults to 'nearest'
  verticalOffset?: number; //Vertical offset in pixels. Defaults to 0
}

/**
 * Custom hook to smoothly scroll to a specific element and focus on the first focusable input field
 *
 * @param elementRef - React RefObject pointing to the DOM element to scroll and focus within
 * @param options - Optional configuration for scroll behavior
 * @returns A tuple: [RefObject<T>, () => void]
 * - The first element is the RefObject to attach to target DOM element
 * - The second element is the function to trigger the scroll and focus behavior
 */
const useScrollToAndFocus = <T extends HTMLElement>(options?: ScrollToAndFocusOptions): [RefObject<T>, () => void] => {
  const internalElementRef = useRef<T>(null);

  const {
    scrollBehavior = 'smooth',
    scrollBlock = 'start',
    scrollInline = 'nearest',
    verticalOffset = 0,
  } = options || {};

  const scrollToAndFocus = useCallback(() => {
    if (internalElementRef.current) {
      const element = internalElementRef.current;
      let originalScrollMarginTop: string | undefined = undefined;

      if (verticalOffset !== 0) {
        originalScrollMarginTop = element.style.scrollMarginTop;
        element.style.scrollMarginTop = `${verticalOffset}px`;
      }

      element.scrollIntoView({
        behavior: scrollBehavior,
        block: scrollBlock,
        inline: scrollInline,
      });

      if (originalScrollMarginTop !== undefined) {
        requestAnimationFrame(() => {
          if (internalElementRef.current) {
            internalElementRef.current.style.scrollMarginTop = originalScrollMarginTop!;
          }
        });
      }

      const focusableElement = element.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), .MuiSelect-select[role="button"][tabindex]:not([disabled])',
      );

      if (focusableElement) {
        focusableElement.focus();
      } else {
        console.warn('useScrollToAndFocus: No specific focusable element found');
      }
    }
  }, [scrollBehavior, scrollBlock, scrollInline, verticalOffset]);

  return [internalElementRef, scrollToAndFocus];
};

export default useScrollToAndFocus;
